import * as React from 'react';
import { connect } from 'react-redux';
import { BaseElement, BaseCheaterContainer, Table } from '../components';
import { ContainerContext, mapProps } from './index';
import { FormControlLabel, FormGroup, Grid2 as Grid, IconButton, Snackbar, Switch, TextareaAutosize, Typography } from '@mui/material';
import { useNavigate } from 'react-router';
import { StateMap } from '../reducers';
import useMediaQuery from '@mui/material/useMediaQuery';
import { StyleDivider } from '../components/common/StyleDivider';
import { Button } from '../components/common/input/Button';
import { ChangeEvent } from 'react';
import { ActionRequest } from '../actions';
import { Clear, Close, CopyAll, Download, FilterList } from '@mui/icons-material';
import { formatDate, truncateCurrencyNumber } from '../utils';

interface Props extends ContainerContext.Props {
}

interface State {
    filterOutCheaters: boolean;
    filterOutBanned: boolean;
    filterOutDevelopers: boolean;
    filterOutSpenders: boolean;
    playerList: string;
    filteredCheaters: string[];
    filteredCheaterData: Record<string,Partial<PlayerCheaterCheckData>>;
    info: string,
}

class Container extends BaseElement<Props, State> {
    private readonly processTableData = (): (PlayerCheaterCheckData&{playerId:string})[] => {
        return this.state.filteredCheaters.map(cheater => {
            const cheaterData = this.state.filteredCheaterData[cheater] ?? {};
            return {
                playerId: cheater,
                accountCreated: cheaterData.accountCreated!,
                valueToDateUSD: cheaterData.valueToDateUSD!,
                shrd_currency_hard: cheaterData.shrd_currency_hard ?? 0,
                shrd_currency_gear: cheaterData.shrd_currency_gear ?? 0,
                mg_content_mg_currency_soft: cheaterData.mg_content_mg_currency_soft ?? 0,
                shrd_currency_tootboot_store: cheaterData.shrd_currency_tootboot_store ?? 0,
                shrd_currency_challenge_ticket_001: cheaterData.shrd_currency_challenge_ticket_001 ?? 0,
                shrd_currency_tootboot_voting_ticket: cheaterData.shrd_currency_tootboot_voting_ticket ?? 0,
                shrd_currency_wildcard: cheaterData.shrd_currency_wildcard ?? 0,
                ru_badges: cheaterData.ru_badges ?? 0,
                mg_content_mg_content: cheaterData.mg_content_mg_content ?? 'unknown',
            };
        });
    }
    state: State = {
        filterOutCheaters: true,
        filterOutBanned: true,
        filterOutDevelopers: true,
        filterOutSpenders: true,
        playerList: '',
        filteredCheaters: [],
        info: '',
        filteredCheaterData: {},
    };
    
    private readonly setFilteredCheaters = (filteredCheaters: string[]) => this.setState({ filteredCheaters });
    private readonly setFilteredCheaterData = (cheaterData: Record<string,Partial<PlayerCheaterCheckData>>) => this.setState({ filteredCheaterData: cheaterData });
    private readonly setInfo = (info: string) => this.setState({ info });

    private readonly outputTextareaRef = React.createRef<HTMLTextAreaElement>();

    protected renderContainer(): React.JSX.Element {
        return (
            <BaseCheaterContainer
                {...this.props}
                styled={this.props.styled}
                title = {"Cheaters"}
                showSubTabs
            >
                {this.renderContent()}
            </BaseCheaterContainer>
        );
    }

    private readonly downloadData = () => {
        const tableData = this.processTableData();
        const rowHeader = [
            'User ID',
            'Account Created',
            'VTD',
            'Gems',
            'RUDOLLAS',
            'LIPSTICKS',
            'TOOT COINS',
            'CHAL TICKETS',
            'VOTING TICKETS',
            'WILD CARD',
            'RuBadge Count',
            'Stage',
        ].join(",") + "\n";
        const output = new Blob([
            rowHeader,
            ...tableData.map((row) => {
                return [
                    row.playerId,
                    formatDate(row.accountCreated, 'L'),
                    truncateCurrencyNumber(row.valueToDateUSD, 'USD'),
                    row.shrd_currency_hard,
                    row.shrd_currency_gear,
                    row.mg_content_mg_currency_soft,
                    row.shrd_currency_tootboot_store,
                    row.shrd_currency_challenge_ticket_001,
                    row.shrd_currency_tootboot_voting_ticket,
                    row.shrd_currency_wildcard,
                    row.ru_badges,
                    row.mg_content_mg_content,
                ].join(",") + "\n";
            })
        ], { type: 'text/csv'});

        const url = URL.createObjectURL(output);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute(
            'download',
            `cheater-filter-${Date.now()}.csv`,
        );
        document.body.appendChild(link);
        link.click();
        link.remove();
    };

    private readonly renderContent = () => {
        const {
            filterOutCheaters,
            filterOutBanned,
            filterOutDevelopers,
            filterOutSpenders,
            playerList,
            filteredCheaters,
            info,
        } = this.state;
        const onToggleCheaterFilter = () => this.setState({ filterOutCheaters: !filterOutCheaters });
        const onToggleBannedFilter = () => this.setState({ filterOutBanned: !filterOutBanned });
        const onToggleDevelopersFilter = () => this.setState({ filterOutDevelopers: !filterOutDevelopers });
        const onToggleSpendersFilter = () => this.setState({ filterOutSpenders: !filterOutSpenders });
        const setPlayerList: React.ChangeEventHandler<HTMLTextAreaElement> = (change: ChangeEvent<HTMLTextAreaElement>) => this.setState({ playerList: change.target.value });
        const cheaterFilterLabel = (filterOutCheaters ? 'Non-' : '') + 'Cheaters';
        const bannedFilterLabel = (filterOutBanned ? 'Non-' : '') + 'Banned';
        const developersFilterLabel = (filterOutDevelopers ? 'Non-' : '') + 'Developers';
        const spendersFilterLabel = (filterOutSpenders ? 'Non-' : '') + 'Spenders';
        return (
            <>
                <div style={{display: "flex"}}>
                    <Grid size={4} paddingLeft={4} paddingRight={4}>
                        <h3>Input</h3>
                        <p>Enter a newline-delimited list of player IDs</p>
                        <TextareaAutosize style={{width: '100%'}} value={playerList} minRows={10} onChange={setPlayerList} name="filterCheaterListInput" /><br />
                        <FormGroup>
                            <FormControlLabel control={<Switch value={filterOutCheaters} onChange={onToggleCheaterFilter} defaultChecked />} label={cheaterFilterLabel} />
                            <FormControlLabel control={<Switch value={filterOutBanned} onChange={onToggleBannedFilter} defaultChecked />} label={bannedFilterLabel} />
                            <FormControlLabel control={<Switch value={filterOutDevelopers} onChange={onToggleDevelopersFilter} defaultChecked />} label={developersFilterLabel} />
                            <FormControlLabel control={<Switch value={filterOutSpenders} onChange={onToggleSpendersFilter} defaultChecked />} label={spendersFilterLabel} />
                        </FormGroup>

                        <Grid container={true} justifyContent="right" spacing={1}>
                            <Button variant="contained" color="secondary" text="Filter" icon={FilterList} onClick={this.filterCheaters} />
                        </Grid>
                    </Grid>
                    <StyleDivider styled={this.props.styled} orientation="vertical" flexItem={true} />
                    <Grid size={8} paddingLeft={4}>
                        <h3>Output</h3>
                        <p>The subset of players who matched all of the filters</p>
                        <TextareaAutosize style={{width: '100%'}} ref={this.outputTextareaRef} value={filteredCheaters.join('\n')} minRows={10} name="filterCheaterListOutput" readOnly />
                        <Grid container={true} justifyContent="right" spacing={1}>
                            <Button variant="contained" color="secondary" text="Copy" icon={CopyAll} onClick={() => this.copyOutput()} />
                            <Button variant="contained" color="secondary" text="Clear Output" icon={Clear} onClick={() => this.setFilteredCheaters([])} />
                        </Grid>
                        <hr />
                        <Grid container={true} justifyContent={"right"} spacing={1}>
                            <Button variant="contained" color="secondary" text="Download CSV" icon={Download} onClick={() => this.downloadData()} />
                        </Grid>
                        <Table
                            options={{
                                pageSize: 10,
                                pageSizeOptions: [10, 50, 100],
                            }}
                            styled={this.props.styled}
                            title={'Filtered Players'}
                            onRowClick={(event: any, rowData: any) => this.toLink(`/players/${rowData.playerId}/overview`)}
                            data={this.processTableData()}
                            columns={[
                                {
                                    title: 'Player ID',
                                    field: 'playerId',
                                },
                                {
                                    title: 'Account Created',
                                    field: 'accountCreated',
                                    render: (rowData: any) => formatDate(rowData.accountCreated),
                                },
                                {
                                    title: 'Value to Date',
                                    field: 'valueToDateUSD',
                                    render: (rowData: any) => truncateCurrencyNumber(rowData.valueToDateUSD, 'USD'),
                                },
                                {
                                    title: 'Gems',
                                    field: 'shrd_currency_hard',
                                },
                                {
                                    title: 'RuDollas',
                                    field: 'shrd_currency_gear',
                                },
                                {
                                    title: 'Lipsticks',
                                    field: 'mg_content_mg_currency_soft',
                                },
                                {
                                    title: 'Toot Coins',
                                    field: 'shrd_currency_tootboot_store',
                                },
                                {
                                    title: 'Challenge Tickets',
                                    field: 'shrd_currency_challenge_ticket_001',
                                },
                                {
                                    title: 'Voting Tickets',
                                    field: 'shrd_currency_tootboot_voting_ticket',
                                },
                                {
                                    title: 'Wild Cards',
                                    field: 'shrd_currency_wildcard',
                                },
                                {
                                    title: 'RuBadges',
                                    field: 'ru_badges',
                                },
                                {
                                    title: 'Stage',
                                    field: 'mg_content_mg_content',
                                },
                            ]}
                        />
                    </Grid>
                </div>

                <Snackbar
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'center',
                    }}
                    open={Boolean(info)}
                    autoHideDuration={600000}
                    onClose={() => this.setInfo('')}
                    message={
                        <Typography color="info" id="message-id">
                            {info}
                        </Typography>
                    }
                    action={[
                        <IconButton key="close" aria-label="Close" color="inherit" onClick={() => this.setInfo('')}>
                            <Close />
                        </IconButton>,
                    ]}
                />
            </>
        );
    };

    private readonly filterCheaters = async () => {
        const {
            filterOutCheaters,
            filterOutBanned,
            filterOutDevelopers,
            filterOutSpenders,
            playerList,
        } = this.state;
        const result = await ActionRequest.post(`cheaters/filter`, {
            filterOutCheaters,
            filterOutBanned,
            filterOutDevelopers,
            filterOutSpenders,
            playerList: playerList.replaceAll(/\s/g, '\n'),
        });
        if (result) {
            this.setFilteredCheaters(result.matched);
            this.setFilteredCheaterData(result.data);
        }
    };

    private readonly copyOutput = async () => {
        const currentTextarea = this.outputTextareaRef.current;
        if (!currentTextarea) {
            return;
        }
        await navigator.clipboard.writeText(this.state.filteredCheaters.join('\n'));
        this.setInfo('Copied!');
    };
}

interface PlayerCheaterCheckData {
    shrd_currency_hard: number;
    shrd_currency_gear: number;
    mg_content_mg_currency_soft: number;
    shrd_currency_tootboot_store: number;
    shrd_currency_challenge_ticket_001: number;
    shrd_currency_tootboot_voting_ticket: number;
    shrd_currency_wildcard: number;
    mg_content_mg_content: string;
    ru_badges: number;
    valueToDateUSD: number;
    accountCreated: number;
}

const mapStateToProps = (state: StateMap): Props => ({
    ...mapProps(state),
});
const AppContainer = (props: Props) =>
{
    const smallUp = useMediaQuery((theme) => theme.breakpoints.up('sm'));
    const navigate = useNavigate();
    return (<Container {...props} styled={{themeMode: props.app.themeMode, smallUp}} navigate={navigate}/>);
};
export default connect(mapStateToProps)(AppContainer);
